import { faCloudArrowUp, faLongArrowLeft, faVideoSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { allTimezones, ITimezoneOption, useTimezoneSelect } from "react-timezone-select";
import BasicAlert from "../../../../../../components/BasicAlert";
import ConditionalSpinner from "../../../../../../components/ConditionalSpinner";
import { IntegrationMediaSource } from "../../../../../../models/media/MediaSource";
import { useGetAllIntegrationMediaQuery } from "../../../../../../store/api/kinesense";
import { DateFormats } from "../../../../../../utilities/dates";
import { Notifications } from "../../../../../../utilities/Notifications/Notifications";
import {
    ImportWizardPagePropsBase,
    NameAndDatePageData as NameAndDateData,
    SourceFile,
    SourceSelectionPageData,
} from "../../ImportWizard";
import SourceFilesList from "./components/SourceFilesList/SourceFilesList";
import "./SourceSelectionPage.scss";

export const COMBINED_FILE_SIZE_LIMIT = +process.env.REACT_APP_KINESENSE_IMPORT_LIMIT_COMBINED_GB * 1024 * 1024 * 1024;

export function getCombinedFileSize(sourceFiles: SourceFile[]) {
    return (sourceFiles ?? []).reduce((acc, f) => acc + f.file.size, 0);
}

interface SourceSelectionPageProps extends ImportWizardPagePropsBase {
    value: SourceSelectionPageData;
}

function SourceSelectionPage(props: SourceSelectionPageProps) {
    // NOTE: separate state necessary for updating multiple files' states at the same time using set state actions
    const [sourceFiles, setSourceFiles] = useState(props.value?.type === "file" ? props.value.sourceFiles : undefined);
    const [overrides, setOverrides] = useState<{ [key: string]: Partial<SourceFile> }>({});
    const isWithinLimit = getCombinedFileSize(sourceFiles) <= COMBINED_FILE_SIZE_LIMIT;

    // Indices of source files selected/highlighted in the UI
    const [selectedFiles, setSelectedFiles] = useState<number[]>([]);
    // Represents whether the currently previewed video source file is supported by the browser
    const [isSupported, setIsSupported] = useState<boolean>(true);

    const firstFile = sourceFiles?.[0];
    const previewFile = sourceFiles?.[selectedFiles?.[0]]?.file ?? firstFile?.file;
    const hasChosenFiles = sourceFiles !== undefined;

    const [baseStartTime, setBaseStartTime] = useState<Dayjs | undefined>(undefined);

    const defaultName = sourceFiles?.[0]?.file.name;
    const defaultStartTime = sourceFiles?.[0]?.start?.value ?? dayjs.utc(sourceFiles?.[0]?.file.lastModified);
    const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Array of strings, where an undefined value means no error, and a string is the error message
    const errorMsgs: string[] = useMemo(() => {
        if (firstFile === undefined) {
            return Array(sourceFiles?.length ?? 0);
        }

        const expected = {
            name: firstFile.file.name,
            width: firstFile.width,
            height: firstFile.height,
            hasAudioTrack: firstFile.hasAudioTrack,
        };

        return sourceFiles.map((s) => {
            if (s.isLoadingFileData) {
                return undefined;
            }

            if (!s.duration) {
                return "This video file is not supported.";
            } else if (expected.height !== s.height || expected.width !== s.width) {
                return `This video does not have the expected resolution of ${expected.width}x${expected.height} pixels.`;
            } else if (s.hasAudioTrack && !expected.hasAudioTrack) {
                return "This video does not have the expected lack of an audio track.";
            } else if (!s.hasAudioTrack && expected.hasAudioTrack) {
                return "This video does not have the expected presence of an audio track.";
            }

            return undefined;
        });
    }, [sourceFiles]);

    useEffect(() => {
        if (baseStartTime === undefined && sourceFiles?.[0]?.start?.value) {
            setBaseStartTime(sourceFiles[0]?.start?.value);
        }
    }, [sourceFiles, baseStartTime === undefined]);

    useEffect(() => {
        if (
            sourceFiles &&
            sourceFiles.length > 1 &&
            sourceFiles.every((f) => f.start !== undefined && f.duration !== undefined)
        ) {
            let previousStart = baseStartTime ?? sourceFiles[0]?.start?.value;
            let previousEnd = previousStart.add(sourceFiles[0]?.duration, "milliseconds");
            const startOverrides = new Map<string, dayjs.Dayjs>();
            startOverrides.set(sourceFiles[0].file.name, previousStart);

            for (const f of sourceFiles.slice(1)) {
                startOverrides.set(f.file.name, previousEnd);

                previousStart = previousEnd;
                previousEnd = previousStart?.add(f.duration, "milliseconds");
            }

            const nextOverrides: { [key: string]: Partial<SourceFile> } = {};
            startOverrides.forEach((v, k) => {
                nextOverrides[k] = { ...(overrides?.[k] ?? {}), start: { value: v, source: "override" } };
            });
            setOverrides(nextOverrides);
        }
    }, [sourceFiles, baseStartTime]);

    useEffect(() => {
        const sourceFilesWithOverridesApplied = sourceFiles?.map((f) => ({
            ...f,
            ...overrides?.[f.file.name],
        }));

        handleDataChanged({
            type: "file",
            sourceFiles: sourceFilesWithOverridesApplied,
        });

        props.validityChanged(
            sourceFiles?.length &&
                isWithinLimit &&
                !firstFile?.isLoadingFileData &&
                errorMsgs.every((s) => s === undefined),
        );
    }, [sourceFiles, baseStartTime, overrides]);

    const videoRef = useRef<HTMLVideoElement>(null);
    const fileDropRef = useRef<HTMLFieldSetElement>(null);

    function onFileSelected(selectedFiles: FileList) {
        const validFiles = [];
        for (const file of selectedFiles) {
            if (!file || !file.type.startsWith("video")) {
                Notifications.notify(
                    "Invalid file type selected",
                    `The selected file, "${file.name}", is not a valid video file. Type of file: "${file.type}"`,
                    "warning",
                    "invalidFileSelected",
                );
                continue;
            }

            if (file.size < 1000) {
                Notifications.notify(
                    "Invalid file selected",
                    `The selected file, "${file.name}", is smaller than the minimum size of 1 KB.`,
                    "warning",
                    "invalidFileSelected",
                );
                continue;
            }

            validFiles.push(file);
        }

        if (!validFiles.length) {
            return;
        }

        let nextFiles: SourceFile[];
        function mapSourceFile(file: File): SourceFile {
            return {
                file,
                duration: undefined,
                start: undefined,
                width: undefined,
                height: undefined,
                hasAudioTrack: undefined,
                isLoadingFileData: true,
            };
        }

        if (sourceFiles === undefined) {
            nextFiles = validFiles.map(mapSourceFile);
        } else {
            nextFiles = [...sourceFiles];
            nextFiles.push(
                ...validFiles
                    .filter((f) => nextFiles.find((ff) => ff.file.name == f.name) === undefined)
                    .map(mapSourceFile),
            );
        }
        setSourceFiles(nextFiles);
    }

    function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
        onFileSelected(e.target.files);
    }

    function handleFileDropHighlight(e: React.DragEvent<HTMLFieldSetElement>) {
        e.preventDefault();
        e.stopPropagation();
        fileDropRef.current.classList.add("highlight");
    }
    function handleFileDropUnhighlight(e: React.DragEvent<HTMLFieldSetElement>) {
        e.preventDefault();
        e.stopPropagation();
        fileDropRef.current.classList.remove("highlight");
    }

    function handleFileDropped(e: React.DragEvent<HTMLFieldSetElement>) {
        handleFileDropUnhighlight(e);
        onFileSelected(e.dataTransfer.files);
    }

    function handleOvercastMediaSelected(media: IntegrationMediaSource) {
        handleDataChanged({ type: "integration", integrationMediaSource: media });
        props.validityChanged(true);
    }

    function handleDataChanged(updatedValues: Partial<SourceSelectionPageData>) {
        const existingValues = props.value;

        if (existingValues && existingValues.name === undefined) {
            updatedValues.name = defaultName;
            updatedValues.startTime = defaultStartTime;
            updatedValues.timezone = defaultTimezone;
        }

        props.dataChanged({
            ...existingValues,
            ...updatedValues,
        } as SourceSelectionPageData);
    }

    function renderSourceFiles() {
        return (
            <div className="gap-4 d-flex flex-column justify-content-center align-items-center">
                <SourceFilesList
                    sourceFiles={sourceFiles}
                    overrides={overrides}
                    setSourceFiles={setSourceFiles}
                    selectedFiles={selectedFiles}
                    setSelectedFiles={setSelectedFiles}
                    resetSourceFiles={() => setSourceFiles(undefined)}
                    resetSelectedFiles={() => setSelectedFiles([])}
                    errorMsgs={errorMsgs}
                />
            </div>
        );
    }

    function renderPreview() {
        return (
            <div className="gap-4 d-flex flex-column justify-content-center align-items-center">
                <video
                    className={!isSupported ? "d-none" : ""}
                    style={{ maxHeight: 500, maxWidth: "100%" }}
                    ref={videoRef}
                    controls
                />

                {!isSupported && (
                    <div className="gap-3 p-4 rounded border d-flex flex-column align-items-center w-50 bg-body-tertiary">
                        <FontAwesomeIcon icon={faVideoSlash} className="fs-2" aria-label="No preview available" />
                        <aside className="text-center">
                            The format of the selected video file is not compatible with your browser. No preview
                            available.
                        </aside>
                    </div>
                )}

                <p className="px-4 m-0 text-truncate mw-100">
                    File being previewed:{" "}
                    <span className="fw-bold" title={previewFile.name}>
                        {previewFile.name}
                    </span>
                </p>
            </div>
        );
    }

    // TODO: ensure DEM integration source works, since there have been many changes made to the
    // import wizard since it was enabled. Changes are also required in the summary page.
    const [showOvercastLibrary, setShowOvercastLibrary] = useState<boolean>(false);
    const {
        data: overcastData,
        isLoading: overcastIsLoading,
        isSuccess: overcastIsSuccess,
    } = useGetAllIntegrationMediaQuery({ integrationId: "overcast" }, { skip: !showOvercastLibrary });
    function handleHideOvercastSelection() {
        setSourceFiles(undefined);
        props.validityChanged(false);
        setShowOvercastLibrary(false);
    }
    function handleShowOvercastSelection() {
        setSourceFiles(undefined);
        props.validityChanged(false);
        setShowOvercastLibrary(true);
    }

    function renderOvercastSource(media: IntegrationMediaSource) {
        return (
            <div key={media.integrationMediaId}>
                <div>
                    <img width={250} src={media.preview.source} />
                </div>
                <div>
                    <span>{media.name}</span>
                    <Form.Check
                        key={media.integrationMediaId}
                        type="radio"
                        onChange={() => {
                            handleOvercastMediaSelected(media);
                        }}
                    />
                </div>
            </div>
        );
    }

    function renderOvercastSourceLibrary() {
        return (
            <div className="gap-2 d-flex flex-column">
                <section className="d-flex">
                    <Button
                        variant="outline-secondary"
                        className="m-0 bg-transparent border-0"
                        title="Return to file selection"
                        onClick={handleHideOvercastSelection}
                    >
                        <FontAwesomeIcon icon={faLongArrowLeft} />
                    </Button>

                    <h5 className="m-0 d-flex align-items-center">Overcast Library</h5>
                </section>
                <section>
                    <ConditionalSpinner isLoading={overcastIsLoading}>
                        {overcastIsSuccess ? (
                            overcastData?.map((media) => renderOvercastSource(media))
                        ) : (
                            <BasicAlert message="There was an error loading Overcast data." />
                        )}
                    </ConditionalSpinner>
                </section>
            </div>
        );
    }

    function handleNameAndDateChanged(data: NameAndDateData) {
        if (data.hasSetTimeManually) {
            const startOverrides = new Map<string, dayjs.Dayjs>();
            startOverrides.set(firstFile.file.name, data.startTime);

            const nextOverrides: { [key: string]: Partial<SourceFile> } = {};
            startOverrides.forEach((v, k) => {
                nextOverrides[k] = { ...(overrides?.[k] ?? {}), start: { value: v, source: "override" } };
            });
            setOverrides(nextOverrides);
        }

        handleDataChanged({ name: data.name, startTime: data.startTime, timezone: data.timezone });
        setBaseStartTime(data.startTime);
    }

    function handleNameAndDateValidityChanged(isValid: boolean) {
        props.validityChanged(isValid);
    }

    useEffect(() => {
        const video = videoRef?.current;

        if (!video || !previewFile) {
            return;
        }

        URL.revokeObjectURL(video.src);
        video.src = URL.createObjectURL(previewFile);

        const handleCanPlay = () => {
            setIsSupported(true);
        };
        const handleError = () => {
            setIsSupported(false);
        };

        video.addEventListener("canplay", handleCanPlay);
        video.addEventListener("error", handleError);

        return () => {
            video?.removeEventListener("canplay", handleCanPlay);
            video?.removeEventListener("error", handleError);
        };
    }, [previewFile?.name]);

    return (
        <div className="gap-2 d-flex flex-column">
            {showOvercastLibrary ? (
                renderOvercastSourceLibrary()
            ) : (
                <>
                    <fieldset
                        className={`gap-2 mb-1 text-center drop-file d-flex align-items-center${
                            hasChosenFiles ? " justify-content-center has-chosen" : " flex-column"
                        }`}
                        ref={fileDropRef}
                        onDragEnter={handleFileDropHighlight}
                        onDragOver={handleFileDropHighlight}
                        onDragLeave={handleFileDropUnhighlight}
                        onDrop={handleFileDropped}
                    >
                        <legend className="visually-hidden">Media source upload</legend>

                        {!hasChosenFiles && <FontAwesomeIcon size="8x" icon={faCloudArrowUp} />}

                        <div className="p-0 m-0">
                            <div style={{ fontSize: "larger" }}>Drag & Drop</div>
                            <span className={hasChosenFiles ? "text-muted small" : ""}>
                                {hasChosenFiles
                                    ? "Additional Files for Multi-Part Video"
                                    : "Video File or Multi-Part Files"}
                            </span>
                        </div>

                        <div className="text-muted m-3">or</div>

                        <Form.Label className="mt-2 btn btn-secondary">
                            <Form.Control
                                // Specific file formats also listed because some files fail to be displayed on Chromium.
                                // Users _can_ work around this by selecting "all file types" or similar in their file explorer,
                                // but it would be ideal to just have all relevant files show up as selectable by default.
                                // NOTE: may need more file extensions specified here in the future
                                accept="video/*, .avi, .mkv, .wmv, .qt, .flv, .swf"
                                type="file"
                                onChange={handleFileSelected}
                                multiple
                                hidden
                                aria-hidden
                            />
                            {hasChosenFiles ? "Browse For Additional Files..." : "Browse Files..."}
                        </Form.Label>

                        {/* <Button onClick={handleShowOvercastSelection}>Select From Overcast Library</Button> */}
                    </fieldset>

                    {hasChosenFiles && sourceFiles?.[0] != undefined && (
                        <>
                            <div className="files-header">Selected Files</div>
                            {renderSourceFiles()}
                            <div className="files-header">Name and Start Time</div>
                            <NameAndDateSection
                                name={props.value.name}
                                startTime={props.value.startTime}
                                timezone={props.value.timezone}
                                defaultName={defaultName}
                                defaultStartTime={defaultStartTime}
                                defaultTimezone={defaultTimezone}
                                validityChanged={handleNameAndDateValidityChanged}
                                dataChanged={handleNameAndDateChanged}
                            />
                            <div className="files-header">Preview</div>
                            {renderPreview()}
                        </>
                    )}
                </>
            )}
        </div>
    );
}

interface NameAndDateSectionProps {
    name: string | undefined;
    startTime: Dayjs | undefined;
    timezone: string;
    defaultName: string | undefined;
    defaultStartTime: Dayjs;
    defaultTimezone: string;
    validityChanged: (isValid: boolean) => void;
    dataChanged: (data: NameAndDateData) => void;
}

function NameAndDateSection(props: NameAndDateSectionProps) {
    const [defaultName, defaultStartTime] = useMemo(() => {
        // FILENAME
        let name = props.defaultName;
        // Remove extension
        const extensionIndex = props.defaultName.lastIndexOf(".");
        if (extensionIndex > 0) {
            name = props.defaultName.substring(0, extensionIndex);
        }

        // START TIME
        let start = props.defaultStartTime;
        // Fallback to current time
        if (!start || !start.isValid()) {
            start = dayjs.utc().set("s", 0).set("ms", 0);
        }

        return [name, start];
    }, [props.defaultName, props.defaultStartTime?.valueOf()]);

    const [name, setName] = useState<string>(props.name ?? defaultName ?? "");
    const [datetime, setDatetime] = useState<Dayjs>(props.startTime ?? defaultStartTime);
    const [isValid, setIsValid] = useState<boolean>(false);
    const [timezone, setTimezone] = useState<string>(
        props.defaultTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    );

    const [dateString, setDateString] = useState<string>(dayjs(datetime).format(DateFormats.yearMonthDay));
    const [timeString, setTimeString] = useState<string>(dayjs(datetime).format(DateFormats.timeSeconds));

    const { options, parseTimezone } = useTimezoneSelect({ labelStyle: "original", timezones: allTimezones });

    const handleNameChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.value;
        setName(name);
    };

    const handleDateChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const dateInputValue = event.target.value;

        if (dateInputValue == undefined || dateInputValue.length == 0) {
            return;
        }

        setDateString(dateInputValue);

        const dateBits = dateInputValue.split("-").map(Number);
        if (dateBits.length != 3 || dateBits.some(isNaN) || dateBits[1] < 1) {
            return;
        }

        setDatetime((old) => {
            const next = old
                .set("year", dateBits[0])
                .set("month", dateBits[1] - 1)
                .set("date", dateBits[2]);

            return next;
        });
    };

    const handleTimeChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        const timeInputValue = event.target.value;

        if (timeInputValue == undefined || timeInputValue.length == 0) {
            return;
        }

        const dateBits = timeInputValue.split(":").map(Number);

        setTimeString(timeInputValue);

        setDatetime((old) => {
            const next = old.set("hours", dateBits[0]).set("minutes", dateBits[1]).set("seconds", dateBits[2]);

            return next;
        });
    };

    const handleTimezoneChanged = (timezone: ITimezoneOption) => {
        setTimezone(timezone.value);
    };

    const updateValidity = () => {
        setIsValid(name != undefined && name.length > 0 && datetime != undefined);
    };

    useEffect(() => {
        props.validityChanged(isValid);
    }, [isValid]);

    useEffect(() => {
        updateValidity();
        props.dataChanged({
            name,
            startTime: datetime,
            timezone: timezone,
            hasSetNameManually: name !== props.defaultName,
            hasSetTimeManually: datetime.valueOf() !== defaultStartTime.valueOf(),
        } as NameAndDateData);
    }, [name, datetime?.valueOf(), timezone]);

    return (
        <div className="nameAndTime">
            <div className="nameHeader">Video Name</div>
            <div className="name">
                <Form.Control value={name} onChange={handleNameChanged} />
            </div>
            <div className="dateHeader">Date</div>
            <div className="date">
                <Form.Control type="date" value={dateString} onChange={handleDateChanged} />
            </div>
            <div className="timeHeader">Time</div>
            <div className="time">
                <Form.Control type="time" value={timeString} onChange={handleTimeChanged} />
            </div>
            <div className="zoneHeader">Timezone</div>
            <div className="zone">
                <Form.Select
                    value={timezone}
                    onChange={(e) => handleTimezoneChanged(parseTimezone(e.currentTarget.value))}
                >
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </Form.Select>
            </div>
        </div>
    );
}

export default SourceSelectionPage;
